import { EditOutlined, PlusOutlined } from "@ant-design/icons"
import axiosInstance from "../../../../axios/axiosInstance"
import type { PlanResponse } from "model/response"
import { useEffect, useState } from "react"
import { Input, Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table/interface"
import PlanFormModal from "./PlanFormModal"
import { ModePage } from "common/enum"
import processError from "../../../../utils/processError"
import useTableConfig from "../../../../hooks/useTableConfig"
import WebViewActiveCheckbox from "../../../common/WebViewActiveCheckbox"

export default function PlanManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig()
  const [keyword, setKeyword] = useState<string>("")
  const [planId, setPlanId] = useState<string>("")
  const [mode, setMode] = useState<ModePage>(ModePage.ADD)
  const [isOpen, setOpenModal] = useState<boolean>(false)

  const [data, setData] = useState<PlanResponse[]>()
  const [loading, setLoading] = useState(false)

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price)
  }

  const columns: ColumnsType<PlanResponse> = [
    {
      title: "Tên gói",
      dataIndex: "name",
      width: "15%",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("name"),
    },
    {
      title: "Giá",
      dataIndex: "price",
      width: "10%",
      align: "right",
      sorter: true,
      sortOrder: getColumnSorter("price"),
      render: (price) => formatPrice(price),
    },
    {
      title: "Điểm miễn phí",
      dataIndex: "pointFree",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("pointFree"),
      render: (value, record) => (
        <span className={record.pointFreeEnable ? "text-green-600" : "text-gray-400"}>
          {value} {record.pointFreeEnable ? "✓" : "✗"}
        </span>
      ),
    },
    {
      title: "Điểm premium",
      dataIndex: "pointPremium",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("pointPremium"),
      render: (value, record) => (
        <span className={record.pointPremiumEnable ? "text-green-600" : "text-gray-400"}>
          {value} {record.pointPremiumEnable ? "✓" : "✗"}
        </span>
      ),
    },
    {
      title: "Loại sản phẩm",
      dataIndex: "productType",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("productType"),
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: "Phổ biến",
      dataIndex: "isPopular",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("isPopular"),
      render: (isPopular) => (isPopular ? <Tag color="green">Có</Tag> : <Tag color="red">Không</Tag>),
    },
    {
      title: "Liên hệ",
      dataIndex: "isContact",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("isContact"),
      render: (isContact) => (isContact ? <Tag color="green">Có</Tag> : <Tag color="red">Không</Tag>),
    },
    {
      title: "Thứ tự",
      dataIndex: "orderNumber",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("orderNumber"),
    },
    {
      title: "Hiển thị",
      dataIndex: "webViewActive",
      align: "center",
      render: (_, record) => (
        <WebViewActiveCheckbox
          currentActiveValue={record.webViewActive}
          itemName={"gói"}
          setLoading={setLoading}
          toggleWebviewActive={async () => togglePlanWebviewActive(record.id)}
          reloadPageData={loadListPlans}
        />
      ),
      sorter: true,
      sortOrder: getColumnSorter("webViewActive"),
    },
    {
      title: "Hành động",
      dataIndex: "action",
      align: "center",
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
        </div>
      ),
    },
  ]

  const moveEditView = async (e: any, record: PlanResponse) => {
    setMode(ModePage.EDIT)
    setOpenModal(true)
    setPlanId(record.id)
  }

  const loadListPlans = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance({
        url: "plan/page",
        params: {
          keyword,
          size: pageSize,
          page: currentPage,
          sortBy,
          direction,
          fromCMS: true,
        },
      })

      if (res.status === 200) {
        setData(res.data.content)
        setTotalElements(res.data.totalElements)
      }
    } catch (err) {
      processError(err)
    }
    setLoading(false)
  }

  const onSearch = (e: any) => {
    setKeyword(e.target.value)
  }

  const addNewPlan = async () => {
    setMode(ModePage.ADD)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleBeforeSuccess = () => {
    setOpenModal(false)
    loadListPlans().then()
  }

  useEffect(() => {
    loadListPlans().then()
  }, [currentPage, pageSize, sortBy, direction, keyword])

  const togglePlanWebviewActive = async (id: string) => {
    await axiosInstance.patch(`plan/${id}/toggle-web-view-active`)
  }

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={() => addNewPlan()}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </div>
      <PlanFormModal
        isOpen={isOpen}
        onCloseModal={handleCloseModal}
        mode={mode}
        onBeforeSuccess={handleBeforeSuccess}
        planId={planId}
      />
    </div>
  )
}
